// Accounts. Every device already has an anonymous Supabase user; "creating an account" attaches an
// email to that same user (so nothing is lost), confirmed with a 6-digit code. Signing in on another
// device with an email that already has an account switches to it, and the two states are merged.
//
// Sign in with Apple: native on iPhone (identity token exchanged with signInWithIdToken), Apple's web
// flow in a browser (redirects away and back). Sign in with Google: the browser redirects the same way;
// on iPhone Google refuses embedded web views, so it runs in Apple's secure sign-in sheet
// (ASWebAuthenticationSession) and the session comes back on the app's URL scheme. Either way the
// device's state merges with the account.
//
// Without a backend (local dev, previews, tests) a stand-in is used when localStorage has
// snapcook_fake_auth=1: any email works with code 123456 and the "server" is localStorage.

import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PersistedState } from '../state/schema';
import { captchaToken } from './captcha';
import { hasBackend } from './config';
import { getAccessToken, getSupabase } from './supabase';

export type EmailMode = 'upgrade' | 'signin';
export type Provider = 'apple' | 'google';
export interface Account { email: string }

export interface AccountService {
  current(): Promise<Account | null>;
  /** Sends a code. 'upgrade' keeps this device's user; 'signin' switches to an existing account. */
  sendCode(email: string): Promise<EmailMode>;
  verifyCode(email: string, code: string, mode: EmailMode): Promise<Account>;
  /** The account, or 'redirect' when the browser is leaving for Apple or Google and will come back signed in. */
  signInWith(provider: Provider): Promise<Account | 'redirect'>;
  signOut(): Promise<void>;
  /** The account's saved state (and remembers its revision for the next push). */
  pull(): Promise<unknown | null>;
  /** Saves only if nothing else saved since the last pull or push; otherwise 'conflict'. */
  push(state: PersistedState): Promise<'saved' | 'conflict'>;
}

export class AccountError extends Error {}
/** Thrown when the person closes the Apple or Google sheet; not an error to show. */
export class AccountCanceled extends Error {}

/** Shown when Apple doesn't share an email (it always gives Supabase one, so this is a fallback). */
const APPLE_ACCOUNT = 'your Apple ID';
const NAME: Record<Provider, string> = { apple: 'Apple', google: 'Google' };

/** The in-app native plugins (ios/App/App/AppDelegate.swift). */
const AppleSignIn = registerPlugin<{ authorize(o: { nonce: string }): Promise<{ identityToken: string; email?: string }> }>('AppleSignIn');
const WebAuth = registerPlugin<{ open(o: { url: string; callbackScheme: string }): Promise<{ url: string }> }>('WebAuth');
/** Where Supabase sends the iPhone app back to (must be in Supabase's Redirect URLs). */
const APP_SCHEME = 'com.snapandcook.app';
const APP_CALLBACK = `${APP_SCHEME}://auth-callback`;

async function sha256Hex(s: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The account revision this device last saw; saves name it so a newer save elsewhere isn't overwritten. */
let baseRev = 0;

const supabaseService: AccountService = {
  async current() {
    const sb = await getSupabase();
    const { data } = await sb.auth.getUser();
    const u = data.user;
    return u && !u.is_anonymous ? { email: u.email || APPLE_ACCOUNT } : null;
  },
  async sendCode(email) {
    await getAccessToken(); // make sure the anonymous user exists, so it can be upgraded
    const sb = await getSupabase();
    const { error } = await sb.auth.updateUser({ email });
    if (!error) return 'upgrade';
    // The email already belongs to an account: sign in to that one instead.
    if (/already|registered|exists/i.test(error.message)) {
      const { error: e2 } = await sb.auth.signInWithOtp({ email, options: { shouldCreateUser: false, captchaToken: await captchaToken() } });
      if (e2) throw new AccountError(friendly(e2.message));
      return 'signin';
    }
    throw new AccountError(friendly(error.message));
  },
  async verifyCode(email, code, mode) {
    const sb = await getSupabase();
    const { data, error } = await sb.auth.verifyOtp({ email, token: code, type: mode === 'upgrade' ? 'email_change' : 'email' });
    if (error || !data.user?.email) throw new AccountError(error ? friendly(error.message) : 'That code didn’t work. Try again.');
    return { email: data.user.email };
  },
  async signInWith(provider) {
    const sb = await getSupabase();
    const failed = `Sign in with ${NAME[provider]} didn’t work. Please try again.`;
    if (Capacitor.isNativePlatform() && provider === 'apple') {
      const raw = crypto.randomUUID();
      let token: string;
      try {
        token = (await AppleSignIn.authorize({ nonce: await sha256Hex(raw) })).identityToken;
      } catch (e) {
        if ((e as { code?: string }).code === 'CANCELED') throw new AccountCanceled();
        throw new AccountError(failed);
      }
      const { data, error } = await sb.auth.signInWithIdToken({ provider: 'apple', token, nonce: raw, options: { captchaToken: await captchaToken() } });
      if (error || !data.user) throw new AccountError(failed);
      return { email: data.user.email || APPLE_ACCOUNT };
    }
    if (Capacitor.isNativePlatform()) {
      const { data, error } = await sb.auth.signInWithOAuth({ provider, options: { redirectTo: APP_CALLBACK, skipBrowserRedirect: true } });
      if (error || !data.url) throw new AccountError(failed);
      let callback: string;
      try {
        callback = (await WebAuth.open({ url: data.url, callbackScheme: APP_SCHEME })).url;
      } catch (e) {
        if ((e as { code?: string }).code === 'CANCELED') throw new AccountCanceled();
        throw new AccountError(failed);
      }
      const back = new URL(callback);
      const params = new URLSearchParams(back.hash.slice(1) || back.search.slice(1));
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      if (!access_token || !refresh_token) throw new AccountError(failed);
      const { data: session, error: e2 } = await sb.auth.setSession({ access_token, refresh_token });
      if (e2 || !session.user) throw new AccountError(failed);
      return { email: session.user.email || APPLE_ACCOUNT };
    }
    const { error } = await sb.auth.signInWithOAuth({ provider, options: { redirectTo: window.location.href } });
    if (error) throw new AccountError(`Sign in with ${NAME[provider]} isn’t available right now. Use your email instead.`);
    return 'redirect';
  },
  async signOut() {
    const sb = await getSupabase();
    await sb.auth.signOut();
    baseRev = 0;
  },
  async pull() {
    const sb = await getSupabase();
    const { data, error } = await sb.from('snapcook_user_state').select('state, rev').maybeSingle();
    if (error) throw new AccountError('Couldn’t load your account. Try again.');
    baseRev = Number(data?.rev ?? 0);
    return data?.state ?? null;
  },
  async push(state) {
    const sb = await getSupabase();
    const { data } = await sb.auth.getSession();
    if (!data.session || data.session.user.is_anonymous) return 'saved';
    const { data: r, error } = await sb.rpc('snapcook_save_state', { p_state: state, p_version: state.version, p_base_rev: baseRev });
    if (error || !r) throw new AccountError('Couldn’t save to your account.');
    const result = r as { ok: boolean; rev: number | null };
    if (!result.ok) return 'conflict';
    baseRev = Number(result.rev);
    return 'saved';
  },
};

function friendly(message: string): string {
  if (/rate|too many|seconds/i.test(message)) return 'Too many tries. Wait a minute and try again.';
  if (/invalid|expired|token/i.test(message)) return 'That code didn’t work or has expired. Check it, or send a new one.';
  if (/email/i.test(message)) return 'Check the email address and try again.';
  return 'Something went wrong. Please try again.';
}

const FAKE = { session: 'snapcook_fake_session', server: 'snapcook_fake_server', accounts: 'snapcook_fake_accounts', revs: 'snapcook_fake_revs' };
let fakeBaseRev = 0;
const ls = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

/** Test and preview stand-in. Never used when a real backend is configured. */
const fakeService: AccountService = {
  async current() { const e = ls.get(FAKE.session); return e ? { email: e } : null; },
  async sendCode(email) {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new AccountError('Check the email address and try again.');
    const known: string[] = JSON.parse(ls.get(FAKE.accounts) ?? '[]');
    return known.includes(email) ? 'signin' : 'upgrade';
  },
  async verifyCode(email, code) {
    if (code !== '123456') throw new AccountError('That code didn’t work or has expired. Check it, or send a new one.');
    const known: string[] = JSON.parse(ls.get(FAKE.accounts) ?? '[]');
    ls.set(FAKE.accounts, JSON.stringify([...new Set([...known, email])]));
    ls.set(FAKE.session, email);
    return { email };
  },
  async signInWith(provider) {
    const email = provider === 'apple' ? 'apple-user@privaterelay.appleid.com' : 'google-user@gmail.com';
    const known: string[] = JSON.parse(ls.get(FAKE.accounts) ?? '[]');
    ls.set(FAKE.accounts, JSON.stringify([...new Set([...known, email])]));
    ls.set(FAKE.session, email);
    return { email };
  },
  async signOut() { ls.del(FAKE.session); fakeBaseRev = 0; },
  async pull() {
    const e = ls.get(FAKE.session);
    const all = JSON.parse(ls.get(FAKE.server) ?? '{}');
    fakeBaseRev = e ? JSON.parse(ls.get(FAKE.revs) ?? '{}')[e] ?? 0 : 0;
    return e ? all[e] ?? null : null;
  },
  async push(state) {
    const e = ls.get(FAKE.session);
    if (!e) return 'saved';
    const revs = JSON.parse(ls.get(FAKE.revs) ?? '{}');
    if ((revs[e] ?? 0) !== fakeBaseRev) return 'conflict';
    const all = JSON.parse(ls.get(FAKE.server) ?? '{}');
    ls.set(FAKE.server, JSON.stringify({ ...all, [e]: state }));
    fakeBaseRev = (revs[e] ?? 0) + 1;
    ls.set(FAKE.revs, JSON.stringify({ ...revs, [e]: fakeBaseRev }));
    return 'saved';
  },
};

/** The account service, or null when accounts aren't available in this build. */
export function accountService(): AccountService | null {
  if (hasBackend) return supabaseService;
  return ls.get('snapcook_fake_auth') === '1' ? fakeService : null;
}
